import { OptimizationObjective } from '~/domain/patient/optimizationObjective';
import { csvEscapeCell } from './handleFileExport';

export type OptimizationObjectivesJsonRow = {
    structure: string;
    type: string;
    volumePercent: number;
    doseGy: number;
    priority: number;
    gEUDa: number;
};

/**
 * Flattens an `OptimizationObjective[]` (nested by structure with sub-rows per
 * constraint) into the canonical export shape used by the optimization
 * objectives JSON file. Output rows are sorted by structure name so the file
 * order is stable across runs.
 */
export function buildOptimizationObjectivesJson(
    objectives: OptimizationObjective[]
): OptimizationObjectivesJsonRow[] {
    const rows: OptimizationObjectivesJsonRow[] = objectives.flatMap((obj) =>
        obj.subRows.map((sub) => ({
            structure: obj.structure,
            type: sub.type,
            volumePercent: sub.volumePercent,
            doseGy: sub.dose,
            priority: sub.priority,
            gEUDa: sub.gEUD,
        }))
    );
    rows.sort((a, b) => a.structure.localeCompare(b.structure));
    return rows;
}

/**
 * Builds the CSV serialization of a dose's optimization objectives. Header
 * order matches the table-panel "Export as CSV" menu so clinicians see the
 * same columns regardless of where they triggered the download.
 */
export function buildOptimizationObjectivesCsv(
    objectives: OptimizationObjective[]
): string {
    const headerRow = [
        'Structure',
        'Type',
        'Volume (%)',
        'gEUD a',
        'Dose (Gy)',
        'Weight',
    ];
    const sorted = objectives
        .flatMap((obj) =>
            obj.subRows.map((sub) => ({ structure: obj.structure, sub }))
        )
        .sort((a, b) => a.structure.localeCompare(b.structure));

    const dataRows = sorted.map(({ structure, sub }) => [
        csvEscapeCell(structure),
        csvEscapeCell(sub.type),
        sub.volumePercent,
        sub.gEUD,
        sub.dose,
        sub.priority,
    ]);

    return [headerRow, ...dataRows].map((row) => row.join(',')).join('\n');
}

/**
 * Builds the file name for a dose's optimization objectives. The plan label
 * is sanitized to a filesystem-safe slug. Defaults to `.json`; pass `'csv'`
 * for the CSV sidecar.
 */
export function buildOptimizationObjectivesFilename(
    planLabel: string | undefined,
    fallback: string,
    extension: 'json' | 'csv' = 'json'
): string {
    const label = planLabel ?? fallback;
    const safeLabel = label.replace(/[^A-Za-z0-9_-]+/g, '_');
    return `optimization_objectives_${safeLabel}.${extension}`;
}
